import { FilterOperators } from "@crispico/foundation-gwt-js";
import { Organization } from "@crispico/foundation-react";
import { apolloClientHolder } from "@crispico/foundation-react/apolloClient";
import { Optional } from "@crispico/foundation-react/CompMeta";
import { PeriodPickerRRC, PeriodPicker } from "@crispico/foundation-react/components/periodPicker/PeriodPicker";
import { entityDescriptors, ID } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import { EntityDescriptor } from "@crispico/foundation-react/entity_crud/EntityDescriptor";
import { FindByFilterParams } from "@crispico/foundation-react/entity_crud/FindByFilterParams";
import { dashboardCalculateForRecordsWidgetEntityDescriptor } from "@crispico/foundation-react/FoundationEntityDescriptors";
import { createSliceFoundation, getBaseImpures, getBaseReducers, PropsFrom } from "@crispico/foundation-react/reduxHelpers";
import { Utils } from "@crispico/foundation-react/utils/Utils";
import { Datum, DatumValue, Serie } from "@nivo/line";
import gql from "graphql-tag";
import _ from "lodash";
import lodash from 'lodash';
import moment from "moment";
import React from "react";
import { NavLink } from "react-router-dom";
import SplitPane from "react-split-pane";
import { Button, Container, Dimmer, Icon, Input, Label, Loader, Segment } from "semantic-ui-react";
import { ChartCurrentSelection } from '../ChartCurrentSelection/ChartCurrentSelection';
import { Filter } from "../CustomQuery/Filter";
import { ResponsiveLineExt } from "../nivoExt";
import { ResponsiveLineChartRRC, ResponsiveLineChart} from "../responsiveLineChart/ResponsiveLineChart";
import { MessageExt } from "../semanticUiReactExt";
import { CalculateForRecordsWidgetConfig, COMMA_SEPARATOR, createChartValuesForParentOrganizations, isCompatibleWithDataExplorerFilter, ORGANIZATION } from "./CalculateForRecords";
import { GET_SAVED_DATA } from "./queries";

export const sliceCalculateForRecordsChart = createSliceFoundation(class SliceCalculateForRecordsChart {
    nestedSlices = {
    }

    initialState = {
        data: [] as Array<Serie>,
        selectedPoint: undefined as Optional<number>,
        ids: {} as { [key: string]: string },
        details: undefined as Optional<Array<any>>,
        status: "",
        quickSearch: "",
        widgetId: undefined
    }

    reducers = {
        ...getBaseReducers<SliceCalculateForRecordsChart>(this),
    }

    impures = {
        ...getBaseImpures<SliceCalculateForRecordsChart>(this),

        async getEntityMiniFields(entityDescriptor: EntityDescriptor, widgetConfig: CalculateForRecordsWidgetConfig, selectedGroupByKey: string) {
            if (this.getState().ids[this.getState().selectedPoint!] === "") {
                this.getDispatchers().setInReduxState({ details: undefined });
                return;
            }

            this.getDispatchers().setInReduxState({ status: "loadingDetails" });
            const loadOperationName = `${lodash.lowerFirst(entityDescriptor.name)}Service_findByFilter`;
            const query = gql(`query q($params: FindByFilterParamsInput) { 
                ${loadOperationName}(params: $params) {
                    results { ${ID} ${entityDescriptor.getGraphQlFieldsToRequest(entityDescriptor.miniFields)} }
                }
            }`);
            let filter = Filter.create(ID, FilterOperators.forNumber.in, this.getState().ids[this.getState().selectedPoint!]);

            if (selectedGroupByKey !== "") {
                filter = Filter.createComposed(FilterOperators.forComposedFilter.and, [filter]);

                const keys = selectedGroupByKey.split(COMMA_SEPARATOR);
                const groupByFieldsArray = widgetConfig.groupByFields.split(COMMA_SEPARATOR);

                for (var i = 0; i < keys.length; i++) {
                    if (widgetConfig.groupBySuborganizations && i === 0) {
                        filter.filters!.push(Filter.create(ORGANIZATION, FilterOperators.forNumber.equals, keys[i]));
                    } else {
                        let incPos = 0;
                        if (widgetConfig.groupBySuborganizations) {
                            incPos = 1;
                        }

                        if (!groupByFieldsArray || i - incPos >= groupByFieldsArray.length) {
                            continue;
                        }

                        filter.filters!.push(Filter.create(groupByFieldsArray[i - incPos], FilterOperators.forNumber.equals, keys[i]));
                    }
                }
            }

            const result = (await apolloClientHolder.apolloClient.query({ query: query, variables: FindByFilterParams.create().filter(filter), context: { showSpinner: false } })).data[loadOperationName];

            this.getDispatchers().setInReduxState({ details: result.results, status: "" });
        }
    }
});

export type CalculateForRecordsChartProps = PropsFrom<typeof sliceCalculateForRecordsChart> & {
    dashboardId: number, widgetIdIfReferenced: string, widgetConfig: CalculateForRecordsWidgetConfig, onlyChart?: boolean, format: string, selectedGroupByKey: string, groupByLabel: React.ReactNode,
    valueIndex: number, percentageMode: Optional<boolean>, currentOrganization: Optional<Organization>, dataExplorerFilter?: Filter, expandedOrganization: Optional<Organization>;
}

export class CalculateForRecordsChart extends React.Component<CalculateForRecordsChartProps> {

    protected periodPickerRef = React.createRef<PeriodPicker>();
    entityDescriptor = entityDescriptors[this.props.widgetConfig.entityType];
    protected responsiveLineChartRef = React.createRef<ResponsiveLineChart>();

    componentDidMount() {
        const { props } = this;
        props.dispatchers.setInReduxState(props.dispatchers.getSlice().initialState);
        this.validateAndGetData();
    }

    componentDidUpdate(prevProps: CalculateForRecordsChartProps) {
        const props = this.props;
        if ((!props.data[0]?.data.length || (props.data[0].data.length === 1 && props.data[0]?.data[0].y === 0)) && (!prevProps || !(prevProps.groupByLabel as any)?.props?.children[0]?.props) && (props.groupByLabel as any)?.props?.children[0]?.props) {
            props.dispatchers.setInReduxState({ data: [{ id: "default", data: [{ x: moment().unix() * 1000, y: (props.groupByLabel as any)?.props?.children[0]?.props?.children }] }] });
        } else if (!props.data[0]?.data.length) {
            props.dispatchers.setInReduxState({ data: [{ id: "default", data: [{ x: moment().unix() * 1000, y: 0 }] }] });
        }
    }

    getSelectedGroupByKeyForFilter() {
        if (this.props.dataExplorerFilter && this.props.widgetConfig.groupBySuborganizations && this.props.widgetConfig.groupByFields) {
            const selectedGroupByKeyForFilter: string[] = [];
            this.props.widgetConfig.groupByFields.split(",").forEach(field => {
                const value = getGroupingForField(this.props.dataExplorerFilter!, field)[0];
                if (!value) {
                    return;
                }
                selectedGroupByKeyForFilter.push(value.split("|/|")[0]);
            });
            return selectedGroupByKeyForFilter.join(",");
        }
        return "";
    }

    renderDetails() {
        if (this.props.status !== "") {
            return (
                <Segment className="wh100">
                    <Dimmer active><Loader size='medium'>{_msg("general.loading")}</Loader></Dimmer>
                </Segment>);
        }

        if (!this.props.selectedPoint) {
            return <p>{_msg("CalculateForRecords.noPointSelected")}</p>
        }

        return this.props.details?.map((value, i) => {
            const label = this.entityDescriptor.toMiniString(value);
            if (this.props.quickSearch && label.toLowerCase().indexOf(this.props.quickSearch.toLowerCase()) < 0) {
                return null;
            }
            return <NavLink key={i} to={this.entityDescriptor.getEntityEditorUrl(value[ID])}>
                <Label style={{ marginBottom: "5px" }} basic horizontal color="blue">
                    {typeof this.entityDescriptor.icon === 'string' ? <Icon name={this.entityDescriptor.icon} /> : this.entityDescriptor.icon}
                    {label}
                </Label>
            </NavLink>
        }
        );
    }

    protected formatY = (datum: DatumValue) => {
        return this.props.percentageMode ? datum.valueOf() + "%" : (this.props.format ? (moment.duration(datum.valueOf(), 'milliseconds') as any).format(this.props.format) : datum.valueOf());
    }

    protected async getData(dashboardId: number, widgetIdIfReferenced: string, calculationLogic: string, selectedGroupByKey: string, valueIndex: number, currentOrganization: Optional<Organization>) {
        this.props.dispatchers.setInReduxState({ status: "loadingHeader" });

        const widgetId = (await apolloClientHolder.apolloClient.query({
            query: gql`
                query dashboardCalculateForRecordsWidgetService_findByFilter($params: FindByFilterParamsInput) {
                    dashboardCalculateForRecordsWidgetService_findByFilter(params: $params) {
                        results {
                            id
                        }
                    }
                }`,
            variables: FindByFilterParams.create().filter(Filter.createComposed(FilterOperators.forComposedFilter.and, [
                Filter.create("dashboard.id", FilterOperators.forNumber.equals, dashboardId.toString()),
                Filter.create("widgetIdIfReferenced", FilterOperators.forNumber.equals, widgetIdIfReferenced),
            ])),
            context: { showSpinner: false }
        })).data.dashboardCalculateForRecordsWidgetService_findByFilter.results[0]?.id;
        if (!widgetId) {
            this.props.dispatchers.setInReduxState({ status: "" });
            return;
        }
        this.props.dispatchers.setInReduxState({ widgetId: widgetId });

        const params = FindByFilterParams.create().sorts([{ field: "timestamp", direction: "ASC" }])
            .filter(Filter.createComposed(FilterOperators.forComposedFilter.and, [
                Filter.create("widget.id", FilterOperators.forNumber.equals, widgetId.toString()),
                Filter.create("timestamp", FilterOperators.forDate.greaterThanOrEqualTo, moment(this.periodPickerRef.current!.getStartDate()).toISOString()),
                Filter.create("timestamp", FilterOperators.forDate.lessThanOrEqualTo, moment(this.periodPickerRef.current!.getEndDate()).toISOString())
            ]));

        const result = (await apolloClientHolder.apolloClient.query({ query: GET_SAVED_DATA, variables: params, context: { showSpinner: false } })).data.dashboardCalculateForRecordsValueService_findByFilter

        const data: Datum[] = [];
        const ids: { [key: string]: string } = {};

        const orgId = global.currentOrganizationToFilterBy ? global.currentOrganizationToFilterBy.id : currentOrganization?.id;
        const points = createChartValuesForParentOrganizations(result.results, calculationLogic, currentOrganization);

        for (const point of points) {
            const date = moment(point.timestamp * 1000).toDate().getTime();
            let value = 0;
            if (orgId && selectedGroupByKey.length > 0 && point.result[orgId + "," + selectedGroupByKey]) {
                value = point.result[orgId + "," + selectedGroupByKey];
            } else if (orgId && selectedGroupByKey.length === 0 && point.result[orgId]) {
                value = point.result[orgId];
            } else if (point.result[selectedGroupByKey]) {
                value = point.result[selectedGroupByKey];
            }
            data.push({ x: date, y: value ? value : 0 });
            ids[date] = point.records;
        }

        // WARNING: id = 0 => not accepted any more by nivo
        this.props.dispatchers.setInReduxState({ data: [{ id: "default", color: "var(--blue)", data }] as Serie[], ids, status: "" });
    }

    validateAndGetData() {
        if (!this.props.dataExplorerFilter || isCompatibleWithDataExplorerFilter(entityDescriptors[this.props.widgetConfig.entityType], this.props.dataExplorerFilter).length === 0) {
            this.getData(this.props.dashboardId, this.props.widgetIdIfReferenced, this.props.widgetConfig.calculationLogic, this.getSelectedGroupByKeyForFilter(), this.props.valueIndex, this.props.currentOrganization);
        }
    }

    render() {
        if (this.props.dataExplorerFilter && isCompatibleWithDataExplorerFilter(entityDescriptors[this.props.widgetConfig.entityType], this.props.dataExplorerFilter).length > 0) {
            return <Container fluid>{_msg("CalculateForRecords.error.history")} {isCompatibleWithDataExplorerFilter(entityDescriptors[this.props.widgetConfig.entityType], this.props.dataExplorerFilter!).join(", ")}.</Container>;
        }
        const chart = <div className="flex-container flex-grow wh100">
            <Dimmer inverted active={this.props.status === "loadingHeader"}><Loader size='medium'>{_msg("general.loading")}</Loader></Dimmer>
            <div className={(this.props.onlyChart ? "CalculateForRecords_hide" : "")}><MessageExt>
                {/* this.props.onlyChart is needed because this RRC is used twice when we enable show chart in widget */}
                <PeriodPickerRRC id={'periodPicker' + this.props.onlyChart + this.props.dashboardId + this.props.widgetIdIfReferenced + this.props.expandedOrganization?.id} ref={this.periodPickerRef}
                    onChange={(startDate, endDate) => this.validateAndGetData()}
                />
                <Button size={"small"} as={NavLink} disabled={!this.props.widgetId} to={dashboardCalculateForRecordsWidgetEntityDescriptor.getEntityEditorUrl(this.props.widgetId)} style={{ float: "right" }} >{_msg("CalculateForRecrods.chart.button")}</Button>
            </MessageExt>
            </div>
            {this.props.onlyChart ? <ResponsiveLineExt data={this.props.data} legends={undefined} margin={{ top: 20, bottom: 20, right: 20, left: 40 }}
                yFormat={this.formatY} axisLeft={{ format: this.formatY }} curve="stepAfter"
                onClick={e => {
                    if (this.props.onlyChart || !e?.data) {
                        return;
                    }
                    this.props.dispatchers.setInReduxState({ selectedPoint: moment(e.data.x).toDate().getTime() });
                    this.props.dispatchers.getEntityMiniFields(this.entityDescriptor, this.props.widgetConfig, this.props.selectedGroupByKey);
                }}
            /> : <Segment basic={this.props.onlyChart} className="flex-grow" style={{ overflow: "hidden" }}>
                <ResponsiveLineChartRRC id={'CRLC' + this.props.dashboardId + this.props.widgetIdIfReferenced + this.props.expandedOrganization?.id}
                    ref = {this.responsiveLineChartRef}
                    initialSeries={this.props.data} data={this.props.data}
                    initialStartDate={moment(this.periodPickerRef.current?.getStartDate()).valueOf()}
                    initialEndDate={moment(this.periodPickerRef.current?.getEndDate()).valueOf()}
                    currentStartDate={moment(this.periodPickerRef.current?.getStartDate()).valueOf()}
                    currentEndDate={moment(this.periodPickerRef.current?.getEndDate()).valueOf()}
                    hasZoomSlider={!this.props.onlyChart} yFormat={this.formatY} axisLeft={{ format: this.formatY }} curve="stepAfter"
                    onClick={e => {
                        if (this.props.onlyChart || !e?.data) {
                            return;
                        }
                        this.props.dispatchers.setInReduxState({ selectedPoint: moment(e.data.x).toDate().getTime() });
                        this.props.dispatchers.getEntityMiniFields(this.entityDescriptor, this.props.widgetConfig, this.props.selectedGroupByKey);
                    }}
                />
            </Segment>}
        </div>;
        if (this.props.onlyChart) {
            return chart;
        }
        return (<SplitPane className="flex-container flex-grow" split="horizontal" defaultSize="45%" style={{ position: "static" }} primary="second">
            {chart}
            <div className="flex-container flex-grow">
                <ChartCurrentSelection date={this.props.selectedPoint ? moment(this.props.selectedPoint).format(Utils.dateTimeFormat) : undefined}
                    contentHeader={<>
                        {this.props.groupByLabel && <Label basic>
                            {this.props.groupByLabel}
                            <Icon name='triangle down' />
                        </Label>}
                        &nbsp;
                        {_msg("CalculateForRecords.quickSearch")} <Input style={{ fontSize: "12px" }} icon='search' value={this.props.quickSearch} onChange={(e, data) => this.props.dispatchers.setInReduxState({ quickSearch: data.value })} />
                    </>}
                    contentMain={
                        <Segment className="HistogramPresenceInTerritoriesTab_segment flex-grow">
                            {this.renderDetails()}
                        </Segment>} />
            </div>
        </SplitPane>);
    }
}

function getGroupingForField(filter: Filter, field: string) {
    if (!filter.enabled) {
        return [];
    }
    if (filter.field === field) {
        return typeof filter.value === "string" ? [filter.value] : [];
    }
    if (filter.filters) {
        let value: string[] = [];
        filter.filters.forEach(filter => {
            value = value.concat(getGroupingForField(filter, field));
        });
        return value;
    }
    return [];
}